import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import emailjs from '@emailjs/browser';
import './Checkout.css';

// Declare grecaptcha type
declare global {
  interface Window {
    grecaptcha: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback': () => void;
      }) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
    };
  }
}

// TODO: Thay thế bằng reCAPTCHA Site Key của bạn
// Lấy key tại: https://www.google.com/recaptcha/admin
// Test key (luôn pass): 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI
const RECAPTCHA_SITE_KEY = '6Le63yssAAAAAMWnFbvIrR3vrJtcGHK4AsE_h0dZ';

// EmailJS Configuration
// TODO: Cấu hình EmailJS tại https://www.emailjs.com/
// 1. Đăng ký tài khoản EmailJS
// 2. Tạo Email Service (Gmail, Outlook, etc.)
// 3. Tạo 2 Email Templates:
//    - Template 1: Email xác nhận cho khách hàng
//    - Template 2: Email thông báo đơn hàng cho người bán
// 4. Thay thế các giá trị bên dưới bằng Public Key, Service ID và Template IDs của bạn
const EMAILJS_PUBLIC_KEY = 'Ux3k7lO-o7n_76aZH'; // Thay bằng Public Key của bạn
const EMAILJS_SERVICE_ID = 'service_n56v6yf'; // Thay bằng Service ID của bạn
const SELLER_EMAIL = 'trantrungg7@gmail.com';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    phone: '',
  });

  const recaptchaRef = useRef<HTMLDivElement>(null);
  const recaptchaWidgetId = useRef<number | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaError, setRecaptchaError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);

  // Initialize reCAPTCHA
  useEffect(() => {
    const loadRecaptcha = () => {
      if (window.grecaptcha && recaptchaRef.current && recaptchaWidgetId.current === null) {
        recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token: string) => {
            setRecaptchaToken(token);
            setRecaptchaError('');
          },
          'expired-callback': () => {
            setRecaptchaToken('');
            setRecaptchaError('reCAPTCHA đã hết hạn. Vui lòng thử lại.');
          },
        });
      }
    };

    // Wait for grecaptcha to be loaded
    if (window.grecaptcha) {
      loadRecaptcha();
    } else {
      const checkInterval = setInterval(() => {
        if (window.grecaptcha) {
          loadRecaptcha();
          clearInterval(checkInterval);
        }
      }, 100);

      return () => clearInterval(checkInterval);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generateOrderId = (): string => {
    return Date.now().toString();
  };


  const sendOrderEmails = async (orderId: string, orderSubtotal: number, orderShipping: number, orderTotal: number) => {
    // Check if EmailJS is configured
    const publicKeyConfigured = EMAILJS_PUBLIC_KEY && !EMAILJS_PUBLIC_KEY.includes('YOUR_');
    const serviceConfigured = EMAILJS_SERVICE_ID && !EMAILJS_SERVICE_ID.includes('YOUR_');
    
    if (!publicKeyConfigured || !serviceConfigured) {
      console.warn('EmailJS chưa được cấu hình đầy đủ. Vui lòng xem file EMAILJS_SETUP.md để cấu hình.');
      return false;
    }

    try {
      // Initialize EmailJS
      emailjs.init(EMAILJS_PUBLIC_KEY);

      const customerName = `${formData.firstName} ${formData.lastName}`;
      const BASE_URL = 'https://monailbox.com/';

      if (!cartItems || cartItems.length === 0) {
        console.warn('Không có sản phẩm trong giỏ hàng');
        return false;
      }

      // Tạo orders array với TẤT CẢ items trong giỏ hàng
      const ordersArray = cartItems.map((item) => {
        // Tạo URL ảnh với base URL cố định (đảm bảo có https://)
        let imageUrl = item.product.image;
        if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
          // Loại bỏ dấu / ở đầu imagePath nếu BASE_URL đã có / ở cuối
          const imagePath = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
          // Đảm bảo BASE_URL có / ở cuối và imagePath không có / ở đầu
          const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
          imageUrl = `${baseUrl}${imagePath}`;
        }
        
        // Tạo tên sản phẩm với size (nếu có)
        let productName = item.product.name;
        if (item.selectedSize) {
          productName = `${productName} - Size: ${item.selectedSize}`;
        }

        return {
          image_url: imageUrl,
          name: productName,
          units: item.quantity.toString(),
          price: `${(item.product.price * item.quantity).toLocaleString('vi-VN')}đ`
        };
      });

      // Email xác nhận cho khách hàng - chỉ gửi các params cần thiết
      // Gửi orders dưới dạng array để EmailJS template có thể loop
      const customerEmailParams: any = {
        order_id: orderId,
        customer_name: customerName,
        customer_phone: formData.phone,
        customer_address: formData.address,
        orders: ordersArray,
        shipping: `${orderShipping.toLocaleString('vi-VN')}đ`,
        total: `${orderTotal.toLocaleString('vi-VN')}đ`,
        email: formData.email,
      };

      // Thêm từng item vào params với format orders[0][image_url], orders[0][name], etc.
      // Để EmailJS template có thể loop qua {{#orders}}...{{/orders}}
      ordersArray.forEach((item, index) => {
        customerEmailParams[`orders[${index}][image_url]`] = item.image_url;
        customerEmailParams[`orders[${index}][name]`] = item.name;
        customerEmailParams[`orders[${index}][units]`] = item.units;
        customerEmailParams[`orders[${index}][price]`] = item.price;
      });

      // Log nội dung email để debug
      console.log('=== EMAIL CONTENT DEBUG ===');
      console.log('Order ID:', orderId);
      console.log('Total Items in Cart:', cartItems.length);
      console.log('Orders Array (All Items):', ordersArray);
      console.log('Shipping:', `${orderShipping.toLocaleString('vi-VN')}đ`);
      console.log('Total:', `${orderTotal.toLocaleString('vi-VN')}đ`);
      console.log('Customer Email:', formData.email);
      console.log('Customer Name:', customerName);
      console.log('Customer Phone:', formData.phone);
      console.log('Customer Address:', formData.address);
      console.log('Full Email Params:', customerEmailParams);
      console.log('===========================');

      // Gửi email cho khách hàng
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        'template_estuz79', // Template ID cho email khách hàng
        customerEmailParams
      );

      // Gửi email cho người bán (sử dụng cùng template và params, chỉ đổi email)
      const sellerEmailParams = {
        ...customerEmailParams,
        email: SELLER_EMAIL, // Gửi đến email người bán
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        'template_estuz79', // Sử dụng cùng template
        sellerEmailParams
      );

      return true;
    } catch (error) {
      console.error('Error sending emails:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setRecaptchaError('Vui lòng xác nhận bạn không phải là robot.');
      return;
    }
    
    setRecaptchaError('');
    
    // Generate order ID
    const orderId = generateOrderId();
    
    // Calculate totals
    const orderSubtotal = getTotalPrice();
    const orderShipping = 30000;
    const orderTotal = orderSubtotal + orderShipping;
    
    // Đánh dấu đơn hàng thành công
    setOrderSuccess(true);
    clearCart();
    
    // Scroll về đầu trang
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Gửi email (không chặn việc hiển thị thông báo nếu email lỗi)
    try {
      await sendOrderEmails(orderId, orderSubtotal, orderShipping, orderTotal);
    } catch (error) {
      console.warn('Đơn hàng đã được đặt nhưng có lỗi khi gửi email:', error);
    }
  };

  if (cartItems.length === 0 && !orderSuccess) {
    return (
      <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
        <h2>Giỏ hàng của bạn đang trống</h2>
        <button onClick={() => navigate('/')} className="btn-primary">Tiếp tục mua sắm</button>
      </div>
    );
  }

  // Hiển thị thông báo thành công
  if (orderSuccess) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="order-success-message">
            <div className="success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2 className="success-title">Cảm ơn bạn đã mua hàng!</h2>
            <p className="success-text">
              Đơn hàng của bạn đã được đặt thành công. Chúng tôi sẽ liên hệ với bạn sớm nhất để xác nhận đơn hàng.
            </p>
            <button onClick={() => navigate('/')} className="btn-continue-shopping">
              Tiếp tục mua sắm
            </button>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = getTotalPrice();
  const shipping = 30000; // Phí vận chuyển mặc định
  const total = subtotal + shipping;

  return (
    <div className="checkout-page">
        <div className="container">
          <h1 className="checkout-title">Thanh toán</h1>
          
          <div className="checkout-content">
            {/* Checkout Form */}
            <div className="checkout-form-section">
              <form onSubmit={handleSubmit} className="checkout-form">
                {/* Contact Information */}
                <div className="form-section">
                  <h2>Thông tin liên hệ</h2>
                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="form-section">
                  <h2>Địa chỉ giao hàng</h2>
                  <div className="form-row">
                    <div className="form-group">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="Họ"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Tên"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      name="address"
                      placeholder="Địa chỉ"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Số điện thoại"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Payment Information */}
                <div className="form-section">
                  <h2>Thanh toán</h2>
                  <div className="payment-method">
                    <label className="payment-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cod"
                        defaultChecked
                        readOnly
                      />
                      <span>Thanh toán khi nhận hàng (COD)</span>
                    </label>
                    <p className="payment-note">Bạn sẽ thanh toán khi nhận được hàng. Nhân viên giao hàng sẽ thu tiền mặt khi giao hàng.</p>
                  </div>
                </div>

                {/* reCAPTCHA */}
                <div className="form-section">
                  <div className="recaptcha-container">
                    <div ref={recaptchaRef} className="recaptcha-widget"></div>
                    {recaptchaError && <span className="recaptcha-error">{recaptchaError}</span>}
                  </div>
                </div>

                <button type="submit" className="btn-complete-order">Hoàn tất đơn hàng</button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="checkout-summary">
              <div className="order-summary">
                <h2>Tóm tắt đơn hàng</h2>
                <div className="order-items">
                  {cartItems.map((item) => (
                    <div key={item.id} className="order-item">
                      <div className="order-item-image">
                        <img src={item.product.image} alt={item.product.name} />
                      </div>
                      <div className="order-item-details">
                        <h4>{item.product.name}</h4>
                        {(item.selectedSize || item.selectedColor || item.selectedMaterial) && (
                          <p className="order-item-variant">
                            {item.selectedSize && `Kích thước: ${item.selectedSize}`}
                            {item.selectedColor && ` / Màu: ${item.selectedColor}`}
                            {item.selectedMaterial && ` / Chất liệu: ${item.selectedMaterial}`}
                          </p>
                        )}
                        <p className="order-item-quantity">Số lượng: {item.quantity}</p>
                        <p className="order-item-price">{(item.product.price * item.quantity).toLocaleString('vi-VN')}đ</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-totals">
                  <div className="total-row">
                    <span>Tạm tính</span>
                    <span>{subtotal.toLocaleString('vi-VN')}đ</span>
                  </div>
                  <div className="total-row">
                    <span>Phí vận chuyển</span>
                    <span>{shipping.toLocaleString('vi-VN')}đ</span>
                  </div>
                  <div className="total-row total-final">
                    <span>Tổng cộng</span>
                    <span>{total.toLocaleString('vi-VN')}đ</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default Checkout;

